import { jsPDF } from "jspdf";
import { Order } from "@/models";
import { DOMMEK_LOGO_BASE64 } from "./dommekLogo";

export const generateInvoicePdf = (order: Order): Blob => {
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const marginLeft = 25;
  const marginRight = 25;
  const contentWidth = pageWidth - marginLeft - marginRight;

  const black: [number, number, number] = [0, 0, 0];
  const darkText: [number, number, number] = [33, 33, 33];
  const lineColor: [number, number, number] = [200, 200, 200];

  let y = 20;

  // --- Logo ---
  // Logo is 816x166 px, render at ~40mm wide
  const logoWidth = 40;
  const logoHeight = (166 / 816) * logoWidth;
  pdf.addImage(
    `data:image/png;base64,${DOMMEK_LOGO_BASE64}`,
    "PNG",
    marginLeft,
    y,
    logoWidth,
    logoHeight
  );

  y += logoHeight + 10;

  // --- INVOICE title ---
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(24);
  pdf.setTextColor(...black);
  const titleText = "INVOICE";
  const titleWidth = pdf.getTextWidth(titleText);
  pdf.text(titleText, (pageWidth - titleWidth) / 2, y);

  y += 14;

  // --- Invoice # and Date ---
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(...darkText);

  const invoiceNumber = `INV-${order.id}`;
  pdf.text(`Invoice #: ${invoiceNumber}`, marginLeft, y);
  y += 5;

  const invoiceDate = new Date(order.created_at).toLocaleDateString("en-US");
  pdf.text(`Date: ${invoiceDate}`, marginLeft, y);
  y += 10;

  // --- Billed To ---
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("Billed To:", marginLeft, y);
  y += 6;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  const customerName = order.customer
    ? `${order.customer.first_name} ${order.customer.last_name}`
    : "N/A";
  pdf.text(customerName, marginLeft, y);
  y += 5;

  if (order.customer?.email) {
    pdf.text(order.customer.email, marginLeft, y);
    y += 5;
  }

  y += 5;

  // --- For Documents From ---
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("For Documents From:", marginLeft, y);
  y += 6;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.text(
    `Association: ${order.association?.name || "Not specified"}`,
    marginLeft,
    y
  );
  y += 10;

  // --- Order Details table ---
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("Order Details:", marginLeft, y);
  y += 6;

  const items = order.items || [];
  const tableLeft = marginLeft;
  const colQuantityX = pageWidth - marginRight - 55;
  const colAmountX = pageWidth - marginRight - 20;
  const rowHeight = 8;

  // Table header
  pdf.setDrawColor(...lineColor);
  pdf.setLineWidth(0.3);
  pdf.rect(tableLeft, y - 5, contentWidth, rowHeight);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text("Item", tableLeft + 3, y);
  pdf.text("Quantity", colQuantityX - 5, y);
  const amountHeaderW = pdf.getTextWidth("Amount");
  pdf.text("Amount", pageWidth - marginRight - amountHeaderW - 3, y);

  y += rowHeight;

  // Table rows
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);

  for (const item of items) {
    pdf.rect(tableLeft, y - 5, contentWidth, rowHeight);

    const label = item.label || "Document Fee";
    pdf.text(label, tableLeft + 3, y);

    const qty = String(item.quantity || 1);
    const qtyW = pdf.getTextWidth(qty);
    pdf.text(qty, colQuantityX - 5 + (pdf.getTextWidth("Quantity") - qtyW) / 2, y);

    const amount = `$${Number(item.amount).toFixed(2)}`;
    const amountW = pdf.getTextWidth(amount);
    pdf.text(amount, pageWidth - marginRight - amountW - 3, y);

    y += rowHeight;
  }

  // Total row
  pdf.rect(tableLeft, y - 5, contentWidth, rowHeight);
  pdf.setFont("helvetica", "bold");
  pdf.text("Total", tableLeft + 3, y);

  const totalText = `$${Number(order.total_amount).toFixed(2)}`;
  const totalW = pdf.getTextWidth(totalText);
  pdf.text(totalText, pageWidth - marginRight - totalW - 3, y);

  y += rowHeight + 6;

  // --- Payment Information ---
  const payment = order.payments?.[0];
  if (payment?.payment_method && payment?.payment_reference) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text("Payment Information:", marginLeft, y);
    y += 6;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(`Payment Method: ${payment.payment_method}`, marginLeft, y);
    y += 5;

    pdf.text("Payment Reference:", marginLeft, y);
    y += 5;

    // Reference can be long, wrap if needed
    const refLines = pdf.splitTextToSize(
      payment.payment_reference,
      contentWidth
    );
    pdf.text(refLines, marginLeft, y);
    y += refLines.length * 5;
  }

  y += 15;

  // --- Thank you ---
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(...darkText);
  const thankYou = "Thank you for your payment!";
  const thankYouWidth = pdf.getTextWidth(thankYou);
  pdf.text(thankYou, (pageWidth - thankYouWidth) / 2, y);

  return pdf.output("blob");
};

export const downloadInvoicePdf = (order: Order): void => {
  const blob = generateInvoicePdf(order);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `invoice-order-${order.id}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
